import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider, SignedIn, SignedOut } from "@clerk/clerk-react";
import { App } from "./App";
import { SignInScreen } from "./auth/SignInScreen";
import { SignUpScreen } from "./auth/SignUpScreen";
import { HoldingPage } from "./pages/HoldingPage";
import { startAlerts } from "./lib/alerts";
import "./brand/tokens.css"; // Mockingbird design tokens + self-hosted fonts (load first)
import "./index.css";
import "./styles.css";

// First, so an error while the app starts is reported too.
startAlerts();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

// Auth is opt-in at build time: when VITE_CLERK_PUBLISHABLE_KEY is set (cloud
// deploy) the app is gated behind Clerk sign-in; when unset (local self-host /
// dev) it runs auth-free, matching the backend's dev-bypass. Same code, two modes.
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Public share links (`/share/:token`) must work with NO login — they're handed
// to narrators/editors who have no account. Detect that path up front and render
// the app WITHOUT the Clerk gate; the share routes hit only token-scoped public
// endpoints, so no auth is ever needed (and no Clerk hook runs on that route).
const isPublicShare =
  typeof window !== "undefined" && window.location.pathname.startsWith("/share/");

// Signed-out visitors land on the pre-launch holding page (waitlist) by default;
// the team reaches the real login at /sign-in via the page's "Sign in" link. A
// tiny path gate (no router needed — App's router lives in the SignedIn branch).
function PublicGate() {
  const [path, setPath] = React.useState(window.location.pathname);
  React.useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // An invited proofer arrives with a Clerk ticket (`?__clerk_ticket=…`) pointed
  // at /sign-up — route them to the sign-up flow (which consumes the ticket),
  // never the waitlist holding page. Also catch the ticket on any path, in case
  // of a stray redirect target.
  const hasTicket = new URLSearchParams(window.location.search).has("__clerk_ticket");
  if (path.startsWith("/sign-up") || hasTicket) return <SignUpScreen />;
  if (path.startsWith("/sign-in") || path.startsWith("/claim/")) return <SignInScreen />;
  return (
    <HoldingPage
      onSignIn={() => {
        window.history.pushState({}, "", "/sign-in");
        setPath("/sign-in");
      }}
    />
  );
}

const tree = CLERK_PUBLISHABLE_KEY && !isPublicShare ? (
  <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/">
    <SignedIn>
      <App />
    </SignedIn>
    <SignedOut>
      <PublicGate />
    </SignedOut>
  </ClerkProvider>
) : (
  <App />
);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>{tree}</QueryClientProvider>
  </React.StrictMode>
);
